/**
 * Echo Hunt -- live (DB-backed) wiring for the CHARACTER POOL (§8.7, P10.5).
 *
 * Characters live in `echoProfiles.characters` (a JSON column), exactly as worlds live in
 * `echoProfiles.worldPool`. There is no `echoCharacters` table, for the same reason there is no
 * `echoWorlds` table: a pool of a handful of editable documents buys nothing from a table.
 *
 * This file also owns the STORY -> CHARACTER resolution, because that is the one place where the pool
 * and the story rows meet: a story carries only `characterId`, so "which character is this round being
 * woven for" is a lookup that has to handle a deleted entry.
 *
 * See `Docs/Plans/Others/2026-10-19_EchoHunt.md` §8.2, §8.7, §16.2 P10.5.
 */
import {GetAsync} from "mobx-graphlink";
import {GetMyEchoProfile} from "./Profiles_Live.js";
import {UpdateEchoProfile} from "../Server/Commands/EchoProfileCommands.js";
import {EchoStory, GetEchoStories} from "../Server/DB/echoStories/@EchoStory.js";
import {MeID} from "../Server/DB/users.js";
import {EchoCharacter, EchoWorld} from "./Types.js";
import {PickRandomCharacter} from "./Worlds.js";
import {liveLLMBackend} from "./LLM_Live.js";
import {GenerateCharacterDraft, GenerateWorldDraft, LoreDraftResult} from "./Lore.js";
import type {LoreCharacterField} from "./Prompts/Lore.js";
import type {LoreWorldField} from "./Prompts/Lore.js";

// the pool (§8.7)
// ==========

/** The profile's characters. Never null: an empty pool yields an empty array, so callers check
 * `.length` instead of null-guarding twice. */
export function GetCharacterPool_Live(): EchoCharacter[] {
	return GetMyEchoProfile()?.characters ?? [];
}

/** True when at least one character exists, i.e. when a NEW story can be started. Deliberately separate
 * from the world-pool check: they fail with different messages and point at different tabs. */
export function IsCharacterPoolReady(): boolean {
	return GetCharacterPool_Live().length > 0;
}

export function GetCharacterById_Live(id: string|n): EchoCharacter|n {
	if (id == null) return null;
	return GetCharacterPool_Live().find(a=>a.id == id) ?? null;
}

/** Adds one character to the pool, or replaces the one with the same id. Returns false when there is no
 * profile to write to. */
export async function UpsertCharacter_Live(character: EchoCharacter): Promise<boolean> {
	const profile = await GetAsync(()=>GetMyEchoProfile());
	if (profile == null) return false;

	const pool = [...(profile.characters ?? [])];
	const index = pool.findIndex(a=>a.id == character.id);
	if (index >= 0) pool[index] = character;
	else pool.push(character);

	await new UpdateEchoProfile({id: profile.id, updates: {characters: pool}}).RunOnServer();
	return true;
}

/** Removes one character from the pool. Unlike `RemoveWorld_Live`, this does NOT refuse the last entry:
 * an empty character pool is a legitimate state ("I am rewriting my cast"), and the only consequence is
 * that a new story cannot start until one is authored. Stories that referenced the removed id simply
 * fail to resolve, which `StoryCharacterMissing` reports. */
export async function RemoveCharacter_Live(characterId: string): Promise<boolean> {
	const profile = await GetAsync(()=>GetMyEchoProfile());
	if (profile == null) return false;

	const pool = (profile.characters ?? []).filter(a=>a.id != characterId);
	await new UpdateEchoProfile({id: profile.id, updates: {characters: pool}}).RunOnServer();
	return true;
}

/** Writes a whole character pool at once. */
export async function SetCharacterPool_Live(characters: EchoCharacter[]): Promise<boolean> {
	const profile = await GetAsync(()=>GetMyEchoProfile());
	if (profile == null) return false;
	await new UpdateEchoProfile({id: profile.id, updates: {characters}}).RunOnServer();
	return true;
}

/** The stories that currently reference a character (§8.7). The editor shows this so the blast radius of
 * an edit -- or a deletion -- is visible rather than implied: a story holds only the id, so editing the
 * entry really does change the running story. */
export function GetStoriesUsingCharacter_Live(characterId: string|n): EchoStory[] {
	const userID = MeID();
	if (characterId == null || userID == null) return [];
	return GetEchoStories(userID).filter(a=>a.characterId == characterId);
}

export function CountStoriesUsingCharacter_Live(characterId: string|n): number {
	return GetStoriesUsingCharacter_Live(characterId).length;
}

/** True when the story has no resolvable character: either none was ever assigned (a story predating the
 * pool) or the entry was deleted. Such a story cannot be played; the picker that repairs it is P11's. */
export function StoryCharacterMissing(story: EchoStory|n): boolean {
	return ResolveStoryCharacter_Live(story) == null;
}

/** The character a story follows, or null when its id no longer resolves. */
export function ResolveStoryCharacter_Live(story: EchoStory|n): EchoCharacter|n {
	if (story == null) return null;
	return GetCharacterById_Live(story.characterId);
}

/** The character the NEXT round of `story` should be woven for (§8.7, §8.3).
 *
 *  - an active story whose character resolves: that character (editing it mid-story is the point);
 *  - an active story whose character was deleted: null, so the caller REFUSES the round instead of
 *    quietly starting a different person's story -- the summary, suspicion and world all belong to the
 *    character that is gone;
 *  - no story at all: a draw from the pool, excluding the previous story's character.
 *
 * Returns null for an empty pool too; the caller must tell the user to author a character.
 *
 * SYNC -- from an async path, wrap the call: `await GetAsync(()=>ResolveNextCharacter_Live(...))`. */
export function ResolveNextCharacter_Live(story: EchoStory|n, previousStory: EchoStory|n, rng: () => number): EchoCharacter|null {
	if (story != null) return ResolveStoryCharacter_Live(story) ?? null;
	return PickRandomCharacter(GetCharacterPool_Live(), rng, previousStory?.characterId);
}

// authoring (§8.7)
// ==========

/** Drafts part of a character through the `loreGenerator` role.
 *
 * Deliberately does NOT write to the profile: the editor owns persistence (Save / Add to pool), so a draft
 * the user dislikes can be abandoned with Cancel. Writing here would make every Draft press mutate the
 * cast, and -- once a story follows the entry -- change a running story as a side effect of a preview.
 *
 * The role's model is resolved here rather than in the UI, so a panel never has to know how models are
 * looked up. */
export async function DraftCharacter_Live(options: {base: EchoCharacter, field: LoreCharacterField, override?: boolean, hint?: string|n}): Promise<LoreDraftResult<EchoCharacter>> {
	const profile = await GetAsync(()=>GetMyEchoProfile());
	return GenerateCharacterDraft(liveLLMBackend, {
		field: options.field,
		base: options.base,
		override: options.override,
		// names already in the cast, so a draft cannot duplicate one. The entry being edited is excluded --
		// otherwise its OWN name would be "taken" and every per-field draft would trip the retry.
		avoidNames: (profile?.characters ?? []).filter(a=>a.id != options.base.id).map(a=>a.name),
		hint: options.hint,
		model: profile?.modelRoles?.loreGenerator ?? null,
	});
}

/** The same for a world (the Worlds tab's "Add world" / per-field draft buttons). Also non-persisting. */
export async function DraftWorld_Live(options: {base: EchoWorld, field: LoreWorldField, override?: boolean, hint?: string|n}): Promise<LoreDraftResult<EchoWorld>> {
	const profile = await GetAsync(()=>GetMyEchoProfile());
	return GenerateWorldDraft(liveLLMBackend, {
		field: options.field,
		base: options.base,
		override: options.override,
		hint: options.hint,
		model: profile?.modelRoles?.loreGenerator ?? null,
	});
}
